/**
 * A small popover for the subtitle buttons (1.9.0): a button that opens a panel placed on the page (fixed, so a cue
 * list that scrolls does not cut it off). The panel is role=dialog, so the asset page's Esc → Library shortcut leaves
 * it alone; Esc (caught before the page's own listeners) closes the panel and gives focus back to the button. A press
 * outside, scrolling the page or resizing it closes it too.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

export function Popover({ label, button, title, children, disabled, testId, className = 'btn ghost sm', width = 236 }: {
  /** The dialog's accessible name. */
  label: string;
  /** What the button shows. */
  button: ReactNode;
  title?: string;
  children: (close: () => void) => ReactNode;
  disabled?: boolean;
  testId?: string;
  className?: string;
  width?: number;
}) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const close = () => setAt(null);
  useDismissOnOutside(anchor, at !== null, close);
  useEffect(() => {
    if (!at) return;
    const onScroll = (e: Event) => { if (!panel.current?.contains(e.target as Node)) setAt(null); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setAt(null);
      btn.current?.focus();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', close); window.removeEventListener('keydown', onKey, true); };
  }, [at]);
  // Kept inside the window once its height is known.
  useLayoutEffect(() => {
    const el = panel.current;
    if (!at || !el) return;
    const r = el.getBoundingClientRect();
    const top = r.bottom > window.innerHeight - 8 ? Math.max(8, at.top - r.height - (btn.current?.offsetHeight ?? 0) - 8) : at.top;
    if (top !== at.top) setAt({ ...at, top });
  }, [at]);
  const open = () => {
    const r = btn.current!.getBoundingClientRect();
    setAt({ top: r.bottom + 4, left: Math.max(8, Math.min(window.innerWidth - width - 8, r.left)) });
  };
  return (
    <span className="sub-popover-anchor" ref={anchor}>
      <button ref={btn} type="button" className={className} title={title} aria-label={title ?? label} aria-haspopup="dialog" aria-expanded={at !== null} disabled={disabled}
        onClick={(e) => { e.stopPropagation(); if (at) close(); else open(); }} data-testid={testId}>{button}</button>
      {at && (
        <div ref={panel} className="sub-popover" role="dialog" aria-label={label} style={{ top: at.top, left: at.left, width }} data-testid={testId ? `${testId}-panel` : undefined}
          onClick={(e) => e.stopPropagation()}>
          {children(close)}
        </div>
      )}
    </span>
  );
}
